import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { subscribeClientPortalUpdates, subscribeClientPortalWork } from "../services/clientPortalService";

function asDate(value) {
  if (!value) return null;
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = asDate(value);
  return date ? date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
}

export default function ClientPortalDashboard() {
  const { userData } = useAuth();
  const [work, setWork] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userData?.clientId) return undefined;
    const a = subscribeClientPortalWork(userData.clientId, setWork, (e) => setError(e.message || "Unable to load work."));
    const b = subscribeClientPortalUpdates(userData.clientId, setUpdates, (e) => setError(e.message || "Unable to load updates."));
    return () => { a(); b(); };
  }, [userData?.clientId]);

  const upcoming = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return work.filter((item) => {
      const date = asDate(item.nextActionDate || item.hearingDate);
      return date && date >= now;
    }).sort((a, b) => (asDate(a.nextActionDate || a.hearingDate)?.getTime() || 0) - (asDate(b.nextActionDate || b.hearingDate)?.getTime() || 0));
  }, [work]);

  const recent = updates.slice(0, 4);

  return (
    <section>
      <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#7F9BB3]">Welcome</p>
      <h1 className="mt-2 text-3xl font-semibold">{userData?.clientName || userData?.name || "Client"}</h1>
      <p className="mt-2 max-w-3xl text-slate-600">A secure view of work updates that your firm has specifically chosen to publish to you.</p>

      {error && <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

      <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <p className="text-sm text-slate-500">Current work items</p>
          <p className="mt-2 text-3xl font-semibold">{work.length}</p>
        </div>
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <p className="text-sm text-slate-500">Upcoming actions</p>
          <p className="mt-2 text-3xl font-semibold">{upcoming.length}</p>
        </div>
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <p className="text-sm text-slate-500">Published updates</p>
          <p className="mt-2 text-3xl font-semibold">{updates.length}</p>
        </div>
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <p className="text-sm text-slate-500">Documents</p>
          <p className="mt-2 text-lg font-semibold text-slate-700">DMS next</p>
        </div>
      </div>

      <div className="mt-7 grid gap-6 lg:grid-cols-5">
        <section className="rounded-2xl border bg-white p-6 shadow-sm lg:col-span-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Current Work</h2>
              <p className="mt-1 text-sm text-slate-500">Only client-approved information is shown here.</p>
            </div>
            <Link to="/client/work-updates" className="text-sm font-semibold text-[#0B1F33] hover:underline">View updates</Link>
          </div>
          <div className="mt-5 space-y-3">
            {!work.length && <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">No work item has been published to your portal yet.</div>}
            {work.slice(0, 5).map((item) => (
              <article key={item.id} className="rounded-xl border p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{item.reference || "Matter"}</p>
                    <h3 className="mt-1 font-semibold">{item.title || "Work item"}</h3>
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{item.clientStatus || "In progress"}</span>
                </div>
                {item.clientUpdate && <p className="mt-3 text-sm leading-6 text-slate-600">{item.clientUpdate}</p>}
                {(item.nextAction || item.nextActionDate) && <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900"><strong>Next:</strong> {item.nextAction || "Action scheduled"}{item.nextActionDate ? ` · ${formatDate(item.nextActionDate)}` : ""}</div>}
              </article>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border bg-white p-6 shadow-sm lg:col-span-2">
          <h2 className="text-lg font-semibold">Recent Updates</h2>
          <p className="mt-1 text-sm text-slate-500">Latest information published by your firm.</p>
          <div className="mt-5 space-y-4">
            {!recent.length && <p className="text-sm text-slate-500">No published updates yet.</p>}
            {recent.map((item) => (
              <div key={item.id} className="border-l-2 border-[#C8A96B] pl-4">
                <p className="text-xs text-slate-400">{formatDate(item.publishedAt)}</p>
                <p className="mt-1 text-sm font-semibold">{item.title || item.reference || "Work update"}</p>
                <p className="mt-1 text-sm text-slate-600">{item.clientUpdate}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </section>
  );
}
